/**
 * Structured Data (JSON-LD) for SEO optimization
 */

export const personStructuredData = {
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": "https://joerg-hoermann.de/#person",
  "name": "Jörg Hörmann",
  "jobTitle": "Executive Coach & Finanzcoach",
  "description": "Executive Coach und Finanzcoach. Spezialist für Katalysator-Führung, systemisches Denken, emotionale Intelligenz und achtsame Selbstführung.",
  "url": "https://joerg-hoermann.de",
  "image": "https://joerg-hoermann.de/images/og-portrait.jpg",
  "knowsAbout": [
    "Executive Coaching",
    "Finanzcoaching",
    "Katalysator-Führung",
    "Emotionale Intelligenz in der Führung",
    "Empathie in der Führung",
    "Körperintelligenz",
    "Psychoneuroimmunologie",
    "Outdoor-Coaching",
    "Zen-Philosophie und achtsame Führung",
    "Selbstführung und Resilienz",
    "Systemisches Denken",
    "Hochsensible Führung",
    "HSP-Coaching für Führungskräfte",
    "Hochsensibilität (HSP) in der Führung",
    "Datenschutz und DSGVO-Compliance",
    "Verhaltensökonomie und Behavioral Finance",
    "Neuroplastizität im Coaching-Kontext",
    "Polyvagal-Theorie und Ko-Regulation",
    "Resilienzentwicklung",
    "Neurodiversität in Führungspositionen",
    "Teamdynamik und psychologische Sicherheit"
  ],
  "sameAs": [
    "https://linkedin.com/in/joerg-hoermann",
    "https://reflexionen-zen.joerg-hoermann.de",
    "https://hochsensible-fuehrung.joerg-hoermann.de"
  ],
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Roth",
    "addressCountry": "DE",
    "addressRegion": "Bayern"
  },
  "hasCredential": [
    {
      "@type": "EducationalOccupationalCredential",
      "name": "Geprüfter Fachwirt für Finanzberatung (IHK)",
      "educationalLevel": "DQR 6",
      "credentialCategory": "Professional Certification",
      "recognizedBy": {
        "@type": "Organization",
        "name": "Industrie- und Handelskammer (IHK)"
      }
    },
    {
      "@type": "EducationalOccupationalCredential",
      "name": "Zertifizierter ETF-Consultant (VDH)",
      "credentialCategory": "Professional Certification"
    },
    {
      "@type": "EducationalOccupationalCredential",
      "name": "Ausbildung zum Supervisor",
      "credentialCategory": "Professional Training"
    }
  ],
};

export const organizationStructuredData = {
  "@context": "https://schema.org",
  "@type": ["ProfessionalService", "LocalBusiness"],
  "additionalType": "https://schema.org/ServiceAreaBusiness",
  "@id": "https://joerg-hoermann.de/#organization",
  "name": "Jörg Hörmann - Executive Coaching & Finanzcoaching",
  "description": "Mobiles Finanzcoaching, Executive Coaching und Outdoor-Coaching in Bayern. Coaching vor Ort beim Kunden, outdoor oder remote – bevorzugt im Umkreis von 140 km um Roth. Executive Coaching deutschlandweit.",
  "url": "https://joerg-hoermann.de",
  "logo": "https://joerg-hoermann.de/images/jh-logo-dark.png",
  "image": "https://joerg-hoermann.de/images/jh-logo-og.jpg",
  "telephone": "+49-9171-9892118",
  "email": "info@joerg-hoermann.de",
  "vatID": "DE346500257",
  "founder": {
    "@type": "Person",
    "@id": "https://joerg-hoermann.de/#person",
    "name": "Jörg Hörmann"
  },
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Roth",
    "postalCode": "91154",
    "addressRegion": "Bayern",
    "addressCountry": "DE"
  },
  "serviceType": [
    "Executive Coaching",
    "Finanzcoaching",
    "Outdoor-Coaching",
    "Katalysator-Führung"
  ],
  "areaServed": [
    { "@type": "State", "name": "Bayern" },
    { "@type": "Country", "name": "Deutschland" }
  ],
  "sameAs": [
    "https://linkedin.com/in/joerg-hoermann",
    "https://reflexionen-zen.joerg-hoermann.de",
    "https://hochsensible-fuehrung.joerg-hoermann.de"
  ]
};

export const websiteStructuredData = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": "https://joerg-hoermann.de/#website",
  "name": "Jörg Hörmann - Executive Coaching & Finanzcoaching",
  "url": "https://joerg-hoermann.de",
  "description": "Executive Coaching, Finanzcoaching und Outdoor-Coaching am Rothsee für Führungskräfte und Selbstentscheider",
  "publisher": {
    "@type": "Person",
    "@id": "https://joerg-hoermann.de/#person",
    "name": "Jörg Hörmann"
  },
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://joerg-hoermann.de/blog?search={search_term_string}",
    "query-input": "required name=search_term_string"
  }
};

/**
 * Speakable specification for key pages.
 * Uses CSS selectors to identify speakable content sections.
 */
export const speakableSpecification = {
  "@type": "SpeakableSpecification",
  "cssSelector": [
    "[data-speakable='headline']",
    "[data-speakable='description']",
    "[data-speakable='summary']",
    "[data-speakable='definition']"
  ]
};

/**
 * WebPage schema with speakable for key landing pages.
 */
/**
 * Thematische Entitäten mit Wikipedia-Verlinkung für AIO-Optimierung.
 * Verwendung als "about" im WebPage-Schema.
 */
export const topicEntities: Record<string, { name: string; sameAs: string }> = {
  finanzcoaching: { name: "Finanzcoaching", sameAs: "https://de.wikipedia.org/wiki/Coaching" },
  executiveCoaching: { name: "Executive Coaching", sameAs: "https://en.wikipedia.org/wiki/Executive_coaching" },
  katalysatorFuehrung: { name: "Katalysator-Führung", sameAs: "https://de.wikipedia.org/wiki/F%C3%BChrung" },
  emotionaleIntelligenz: { name: "Emotionale Intelligenz", sameAs: "https://de.wikipedia.org/wiki/Emotionale_Intelligenz" },
  outdoorCoaching: { name: "Outdoor-Coaching", sameAs: "https://de.wikipedia.org/wiki/Erlebnistherapie" },
  achtsamkeit: { name: "Achtsamkeit", sameAs: "https://de.wikipedia.org/wiki/Achtsamkeit" },
  resilienz: { name: "Resilienz", sameAs: "https://de.wikipedia.org/wiki/Resilienz_(Psychologie)" },
  selbstfuehrung: { name: "Selbstführung", sameAs: "https://de.wikipedia.org/wiki/Selbstmanagement" },
  verhaltungsoekonomie: { name: "Verhaltensökonomie", sameAs: "https://de.wikipedia.org/wiki/Verhaltens%C3%B6konomik" },
  burnout: { name: "Burnout", sameAs: "https://de.wikipedia.org/wiki/Burn-out" },
  koerperintelligenz: { name: "Körperintelligenz", sameAs: "https://de.wikipedia.org/wiki/Somatische_Marker" },
  neuroplastizitaet: { name: "Neuroplastizität", sameAs: "https://de.wikipedia.org/wiki/Neuronale_Plastizit%C3%A4t" },
  psychologischeSicherheit: { name: "Psychologische Sicherheit", sameAs: "https://en.wikipedia.org/wiki/Psychological_safety" },
  koRegulation: { name: "Ko-Regulation", sameAs: "https://en.wikipedia.org/wiki/Co-regulation" },
  selbstbestimmungstheorie: { name: "Selbstbestimmungstheorie", sameAs: "https://de.wikipedia.org/wiki/Selbstbestimmungstheorie" },
  fuehrungskompetenz: { name: "Führungskompetenz", sameAs: "https://de.wikipedia.org/wiki/F%C3%BChrungskompetenz" },
  hochsensibilitaet: { name: "Hochsensibilität", sameAs: "https://de.wikipedia.org/wiki/Hochsensibilit%C3%A4t" },
  aktivierungsenergie: { name: "Aktivierungsenergie", sameAs: "https://de.wikipedia.org/wiki/Aktivierungsenergie" },
  eisbergmodell: { name: "Eisbergmodell", sameAs: "https://de.wikipedia.org/wiki/Eisbergmodell" },
  entscheidungsmuedigkeit: { name: "Entscheidungsmüdigkeit", sameAs: "https://en.wikipedia.org/wiki/Decision_fatigue" },
  polyvagalTheorie: { name: "Polyvagal-Theorie", sameAs: "https://en.wikipedia.org/wiki/Polyvagal_theory" },
  systemtheorie: { name: "Systemtheorie", sameAs: "https://de.wikipedia.org/wiki/Systemtheorie" },
};

export const webPageStructuredData = (page: {
  name: string;
  description: string;
  url: string;
  aboutTopics?: string[];
}) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${page.url}#webpage`,
  "name": page.name,
  "description": page.description,
  "url": page.url,
  "isPartOf": {
    "@type": "WebSite",
    "@id": "https://joerg-hoermann.de"
  },
  "speakable": speakableSpecification,
  "mainEntity": {
    "@type": "Person",
    "@id": "https://joerg-hoermann.de/#person"
  },
  ...(page.aboutTopics && page.aboutTopics.length > 0 && {
    "about": page.aboutTopics
      .map(key => topicEntities[key])
      .filter(Boolean)
      .map(topic => ({
        "@type": "Thing",
        "name": topic.name,
        "sameAs": topic.sameAs
      }))
  })
});

export const breadcrumbStructuredData = (items: Array<{name: string, url: string}>) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});

export const articleStructuredData = (article: {
  title: string;
  description: string;
  author: string;
  datePublished: string;
  dateModified?: string;
  url: string;
  image?: string;
  articleBody?: string;
  category?: string;
  tags?: string[];
  aboutTopics?: string[];
}) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": article.title,
  "description": article.description,
  "author": {
    "@type": "Person",
    "@id": "https://joerg-hoermann.de/#person",
    "name": article.author
  },
  "publisher": {
    "@type": "Person",
    "@id": "https://joerg-hoermann.de/#person",
    "name": "Jörg Hörmann",
    "url": "https://joerg-hoermann.de"
  },
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": article.url
  },
  "datePublished": article.datePublished,
  "dateModified": article.dateModified || article.datePublished,
  "url": article.url,
  "image": article.image || "https://joerg-hoermann.de/images/og-portrait.jpg",
  ...(article.articleBody && { "articleBody": article.articleBody }),
  "about": [
    ...(article.aboutTopics || [])
      .map(key => topicEntities[key])
      .filter(Boolean)
      .map(topic => ({
        "@type": "Thing",
        "name": topic.name,
        "sameAs": topic.sameAs
      })),
    ...(article.category ? [{ "@type": "Thing", "name": article.category }] : []),
    ...(article.tags || [])
      .filter(tag => !(article.aboutTopics || []).some(key => topicEntities[key]?.name === tag))
      .map(tag => ({ "@type": "Thing", "name": tag }))
  ],
  "speakable": {
    "@type": "SpeakableSpecification",
    "cssSelector": ["[data-speakable='headline']", "[data-speakable='description']", "[data-speakable='summary']"]
  }
});